import _ from 'lodash';

import { COMMON_DATE_FORMAT } from 'constants/date';
import { formatCalendarDate } from 'utils/date';

import {
  CREDIT_REPORT_DATE_ROWS,
  CREDIT_REPORT_NOT_READ_TEXT,
  EVENT_DATE_FIELDS,
  NO_CREDIT_REPORT_EVENTS_TEXT,
  REFRESH_FALLBACK_MESSAGE,
  REFRESH_STATUS_MESSAGE,
  REPORT_ID_PREFIX_LENGTH,
} from './CreditReportEventDates.constants';

const CALENDAR_DAY_LENGTH = 10;

const toCalendarDay = value => (value ? String(value).slice(0, CALENDAR_DAY_LENGTH) : null);

// The stored reliability is the lowest across the dates; a date's own one comes from the
// derogatory event it was derived from.
const getDateReliability = ({ eventType, date, derogatoryEvents, fallbackReliability }) => {
  const day = toCalendarDay(date);
  const sourceEvent = derogatoryEvents.find(
    event =>
      event.eventType === eventType &&
      EVENT_DATE_FIELDS.some(field => toCalendarDay(event[field]) === day),
  );

  return sourceEvent?.reliability ?? fallbackReliability ?? null;
};

/**
 * The credit-report event dates a borrower carries, each with the reliability of the event it came
 * from, in the order the loan officer's own dates are listed.
 */
export const getCreditReportDateRows = finances => {
  const derogatoryEvents = finances?.derogatoryEvents ?? [];

  return CREDIT_REPORT_DATE_ROWS.filter(({ key }) => !_.isNil(finances?.[key])).map(
    ({ key, label, eventType, hasChapter }) => ({
      key,
      label,
      date: formatCalendarDate(finances[key], COMMON_DATE_FORMAT),
      detail:
        hasChapter && finances.creditBankruptcyChapter
          ? `Chapter ${finances.creditBankruptcyChapter}`
          : null,
      reliability: getDateReliability({
        eventType,
        date: finances[key],
        derogatoryEvents,
        fallbackReliability: finances.creditEventReliability,
      }),
    }),
  );
};

// Without a read date the report was never read for events, so "none on the report" isn't known yet.
export const getNoCreditReportDatesText = finances =>
  finances?.creditEventsUpdatedDate ? NO_CREDIT_REPORT_EVENTS_TEXT : CREDIT_REPORT_NOT_READ_TEXT;

export const getCreditReportSourceText = ({ finances, creditReportReferenceNumber }) => {
  const reportId = finances?.creditEventsCreditReportId;

  if (!reportId) {
    return null;
  }

  const reportName = creditReportReferenceNumber
    ? `Credit report #${creditReportReferenceNumber}`
    : `Credit report ${String(reportId).slice(0, REPORT_ID_PREFIX_LENGTH)}`;
  const updatedDate = finances.creditEventsUpdatedDate
    ? ` · updated ${formatCalendarDate(finances.creditEventsUpdatedDate, COMMON_DATE_FORMAT)}`
    : '';

  return `${reportName}${updatedDate}`;
};

export const getRefreshResultMessage = ({ result, borrowerId }) => {
  const outcome = result?.borrowers?.find(borrower => borrower.borrowerId === borrowerId);
  const getMessage = REFRESH_STATUS_MESSAGE[outcome?.status];

  return getMessage ? getMessage(outcome.eventCount ?? 0) : REFRESH_FALLBACK_MESSAGE;
};
